import React from 'react';
import TrafficLandingTemplate, { LandingPageConfig } from './TrafficLandingTemplate';
import { Activity, BarChart3, Globe } from 'lucide-react';

const config: LandingPageConfig = {
    slug: 'cs/learn/what-is-social-traffic',
    titleTag: 'What is Social Traffic? The Ultimate Guide 2026 | Traffic Creator',
    metaDescription: 'Understand the difference between social, referral, and organic traffic. Learn how to optimize your traffic sources to improve SEO and site authority.',
    keywords: 'What is Social Traffic, Referral Traffic Definition, Types of Website Traffic, Bot Traffic vs Real Traffic, Educational Traffic Guide',
    primaryKeyword: 'What is Social Traffic',
    secondaryKeyword: 'Referral Traffic Definition',
    reviewCount: '465',
    ratingValue: '4.9',

    h1: 'What Is Social & Referral Traffic? - The Ultimate Educational Guide',
    subheadline: 'If you want to understand referral traffic definitions and what social traffic really means for your website, our guide makes it easier than ever.',

    problemH2: 'Why Confusing Traffic Types is Holding You Back',
    problemText: 'A large part of website analytics is properly attributing where your visitors come from. When you confuse organic traffic, direct traffic, referral traffic, and social traffic, you misallocate marketing budgets and hurt your long-term SEO strategy.',
    solutionPoints: [
        'Clearly define the major types of website traffic: Social, Referral, Direct, and Organic',
        'Learn how bot traffic vs. real traffic impacts your Google Analytics 4 (GA4) data',
        'Understand exactly how Traffic Creator can safely simulate social and referral channels',
        'Discover the SEO benefits of diversifying your traffic sources properly'
    ],

    featuresH2: 'Understanding the Types of Website Traffic',
    features: [
        { icon: <Globe />, title: 'Social Traffic', description: 'What is social traffic? It refers to visitors arriving from platforms like Facebook, Twitter, Instagram, or YouTube. A high percentage shows strong brand presence.' },
        { icon: <Activity />, title: 'Referral Traffic Definition', description: 'Referral traffic is any visit arriving from an external site (excluding search engines and social networks). It indicates your content is trusted and linked heavily.' },
        { icon: <BarChart3 />, title: 'Bot Traffic vs Real Traffic', description: 'Good bots (like Googlebot) crawl your site. Bad bots try to ruin analytics. Real residential traffic mimics human behavior safely to boost metrics.' }
    ],

    testimonialsH2: 'What Our Clients Say About Traffic Creator',
    testimonials: [
        { name: 'David M.', role: 'Digital Marketer', result: 'Balanced Analytics', text: 'I used to struggle with 100% direct traffic. Traffic Creator allowed me to buy targeted referral and social traffic, balancing my GA4 profile beautifully.', seed: 'davidm' },
        { name: 'Sarah K.', role: 'SEO Agency', result: 'Improved Trust', text: 'Understanding the referral traffic definition helped me explain to my clients why they needed diversified traffic sources. Traffic Creator delivers exactly that.', seed: 'sarahk' },
        { name: 'Jessica T.', role: 'Blog Owner', result: 'Safe Traffic', text: 'I was worried about bot traffic vs real traffic until I read this guide. The residential IPs from Traffic Creator are so safe and effective for SEO.', seed: 'jessicat' }
    ],

    ctaH2: 'Ready to Boost Your Social and Referral Metrics?',

    faqs: [
        { question: 'What exactly is social traffic and why do I need it?', answer: 'Social traffic is defined as visitors finding your website from social media platforms. You need it because search engines treat strong social signals and traffic as a metric of authority and popularity.' },
        { question: 'What is the standard referral traffic definition?', answer: 'Referral traffic occurs when a user clicks a link on another site (not a search engine) and lands on yours. It shows your site is being discussed across the web.' },
        { question: 'How is bot traffic different from real residential traffic?', answer: 'Dangerous bot traffic comes from datacenter IPs and shows 100% bounce rates. Real residential traffic uses household IPs, scrolls pages, clicks links, and appears completely natural to analytics.' },
        { question: 'Can Traffic Creator generate social and referral traffic?', answer: 'Yes! Our custom campaigns let you set the referrer. You can enter URLs like facebook.com or Reddit.com so the traffic appears as social or referral in your Google Analytics.' },
        { question: 'Is it safe to buy social and referral traffic?', answer: 'Yes, provided you use high-quality residential IPs (like Traffic Creator uses). We bypass bot detectors and look completely natural, keeping your site safe while boosting its metrics.' }
    ],

    relatedLinks: [
        { label: 'Social Media Traffic Signals', to: '/cs/social-media-traffic-bot' },
        { label: 'Website Traffic Generator', to: '/cs/website-traffic-generator' },
        { label: 'High Quality Traffic', to: '/cs/high-quality-website-traffic' },
    ],

    productName: 'What is Social & Referral Traffic? | The Ultimate Guide 2026',
    productDescription: 'Understand the difference between social, referral, and organic traffic. Learn how to optimize your traffic sources to improve your SEO and site authority.',
    translations: {
        'navFeatures': 'Funkce',
        'navPricing': 'Ceník',
        'navFaq': 'FAQ',
        'navLogin': 'Přihlášení',
        'heroBadge': 'Prémiová Služba Provozu',
        'heroCtaPrimary': 'Vyzkoušejte Zdarma',
        'heroCtaSecondary': 'Zobrazit Ceny',
        'heroTrust': 'Důvěřuje nám více než 10 000 uživatelů',
        'heroMicrocopy': 'Kreditní karta není potřeba. 100% Bezpečné a Ověřené v Analytics.',
        'heroLiveLabel': 'Živě',
        'heroTrafficIncrease': 'Nárůst Provozu',
        'heroActiveLabel': 'Aktivní',
        'heroVisitorsServed': 'Doručení Návštěvníci',
        'socialProofLabel': 'Důvěřují nám:',
        'problemWithout': 'Bez',
        'problemWith': 'S',
        'problemBullets': [
                'Nízký organický provoz omezuje váš růst',
                'Špatné pozice ve vyhledávání poškozují konverze',
                'Konkurenti dominují vašim klíčovým slovům',
                'Vysoká míra okamžitého opuštění škodí SEO',
        ],
        'statsCampaigns': 'Denně Aktivních Kampaní',
        'statsCountries': 'Zemí',
        'statsUptime': 'Dostupnost',
        'statsSupport': 'Podpora',
        'featuresSubtitle': 'Vše, co potřebujete pro zlepšení výkonu vašeho webu.',
        'howItWorksH2': '3 Jednoduché Kroky',
        'howItWorksSteps': [
                {
                        'title': 'Nastavte Kampaň',
                        'desc': 'Vyberte zemi, zařízení a nastavení chování během několika minut.'
                },
                {
                        'title': 'Spusťte Síť',
                        'desc': 'Naše platforma směruje skutečné návštěvníky na vaši URL.'
                },
                {
                        'title': 'Analyzujte Výsledky',
                        'desc': 'Sledujte zlepšení svých metrik v Google Analytics 4.'
                },
        ],
        'pricingH2': 'Transparentní',
        'pricingSubtitle': 'Jednorázový nákup. Žádné předplatné. Kredity nikdy nevyprší.',
        'pricingMostPopular': 'Nejoblíbenější',
        'pricingVisitors': 'návštěvníků',
        'pricingGetStarted': 'Začněte Nyní',
        'pricingGuarantee': '30denní Záruka Vrácení Peněz',
        'pricingDelivery': 'Okamžité Doručení (~5 min)',
        'pricingCountries': '190+ Zemí',
        'faqH2': 'Často Kladené Otázky',
        'ctaSubtitle': 'Připojte se k více než 10 000 zákazníkům a zvyšte svůj návštěvnost webu ještě dnes.',
        'ctaButtonPrimary': 'Začněte Zdarma',
        'ctaButtonSecondary': 'Zobrazit Ceny',
        'ctaMicrocopy': 'Kreditní karta není potřeba. 100% Bezpečné a Ověřené v Analytics.',
        'footerDescription': 'řešení pro SEO profesionály a digitální agentury po celém světě.',
        'footerRelatedServices': 'Související Služby',
        'footerCompany': 'Společnost',
        'footerAbout': 'O Nás',
        'footerBlog': 'Blog',
        'footerHelpCenter': 'Centrum Nápovědy',
        'footerPrivacy': 'Zásady Ochrany Osobních Údajů',
        'footerImpressum': 'Právní Informace',
        'footerStatus': 'Všechny Systémy v Provozu',
        'footerBuyTraffic': 'Kupte si Provoz na Web',
    },
};

const CsWhatIsSocialTraffic: React.FC = () => {
    return <TrafficLandingTemplate config={config} />;
};

export default CsWhatIsSocialTraffic;
